import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppBranding } from '../../../../config/branding';
import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  RequestTransactionsInterFace,
  TransactionsInterface,
} from '../../../../interface/transactions-station-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationDetails } from '../../../../redux/slice/NotificationSlice';
import {
  disableTransactionFiltersApplied,
  fetchTransactions,
  fetchTransactionsFilters,
  updateTransactionsFilters,
  setSortData,
} from '../../../../redux/slice/TransactionsStationSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import { FormatUtcString } from '../../../../shared-functions/FormatUtcString';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import stationManagementApi from '../../../../utils/api/station-management';

import TransactionCharts from './TransactionCharts';

/**
 * Transaction component
 * @returns {React.JSX.Element}
 */
function Transactions() {
  const { t } = useTranslation('stationManagement');
  const dispatch = useAppDispatch();
  const branding = useAppBranding();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    transactions,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.transactions);
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);

  const column = [
    {
      label: t('date'),
      accessorKey: 'createdAt' as keyof TransactionsInterface,
      sortAllow: true,
      /**
       * Converting timestamp to UTC date
       * @param {TransactionsInterface} stationActivity
       * @returns {React.JSX.Element}
       */
      customData: (stationActivity: TransactionsInterface) => (
        <p>{ConvertUTCtoDate(stationActivity.createdAt)}</p>
      ),
    },
    {
      label: t('time'),
      accessorKey: 'time' as keyof TransactionsInterface,
      sortAllow: true,
      /**
       * Converting timestamp to UTC time
       * @param {TransactionsInterface} stationActivityTime
       * @returns {React.JSX.Element}
       */
      customData: (stationActivityTime: TransactionsInterface) => (
        <p>{FormatUtcString(ConvertUTCtoHHMM(stationActivityTime.createdAt))}</p>
      ),
    },
    {
      label: t('userName'),
      accessorKey: 'username' as keyof TransactionsInterface,
      sortAllow: true,
    },
    {
      label: t('connectors'),
      accessorKey: 'connector' as keyof TransactionsInterface,
    },
    {
      label: t('amount'),
      accessorKey: 'foRevenue' as keyof TransactionsInterface,
      sortAllow: true,
      /**
       * KWh Consumed
       * @param {TransactionsInterface} currentRow
       * @returns {React.JSX.Element}
       */
      customData: (currentRow: TransactionsInterface) => {
        return (
          <div className="amount-in-transaction-table">
            <p className="table-row-each-data">
              {ToFixedValue(currentRow.foRevenue)}
            </p>
            <span>{branding.text.currency}</span>
          </div>
        );
      },
    },
    {
      label: t('paymentStatus'),
      accessorKey: 'paymentStatus' as keyof TransactionsInterface,
      /**
       * Converting timestamp to UTC time
       * @param {TransactionsInterface} stationActivity
       * @returns {React.JSX.Element}
       */
      customData: (stationActivity: TransactionsInterface) => {
        const type = stationActivity.paymentStatus?.toLowerCase();

        return (
          <p className={`${type === 'processed' ? 'type-in' : 'type-out'}`}>
            {type === 'processed' ? t('paid') : t('failed')}
          </p>
        );
      },
    },
    {
      label: t('energyConsumed'),
      accessorKey: 'powerConsumed' as keyof TransactionsInterface,
      sortAllow: true,
      /**
       * KWh Consumed
       * @param {TransactionsInterface} currentRow
       * @returns {React.JSX.Element}
       */
      customData: (currentRow: TransactionsInterface) => {
        const value = Number(currentRow?.powerConsumed) / 1000;
        return (
          <div className="amount-in-transaction-table">
            <p className="table-row-each-data">
              {ToFixedValue(value)}
            </p>
            <span>{t('kwh')}</span>
          </div>
        );
      },
    },
  ];

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'createdAt' },
    { label: t('time'), accessorKey: 'time' },
    { label: t('userName'), accessorKey: 'username' },
    { label: t('connectors'), accessorKey: 'connector' },
    { label: t('amount'), accessorKey: 'foRevenue' },
    { label: t('paymentStatus'), accessorKey: 'paymentStatus' },
    { label: t('energyConsumed'), accessorKey: 'powerConsumed' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function for pdf and csv
   * @param {string} type pdf or csv
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    const payload = {
      ...queryParameters,
      chargingStation: selectedChargingPoint?.id,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    stationManagementApi
      .fetchTransactionListExport(payload)
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: TransactionsInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'date':
                return ConvertUTCtoDate(row.createdAt) || 'N/A';
              case 'time':
                return ConvertUTCtoHHMM(row.createdAt) || 'N/A';
              case 'foRevenue':
                return `${ToFixedValue(row.foRevenue)} ${branding.text.currency}` || '0.00';
              case 'powerConsumed':
                return `${ToFixedValue(Number(row?.powerConsumed) / 1000)} kWh` || '0.00';
              case 'paymentStatus':
                return row.paymentStatus === 'processed' ? 'Paid' : 'Failed';
              default:
                return (row as any)[col.accessorKey] || 'N/A';
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, t('chargingStationApprovedCSV'));
        } else {
          exportPDF(headers, rows, t('chargingStationApprovedPDF'));
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Handle the fetch the data in api function
   * @param {RequestTransactionsInterFace} data
   */
  const fetchData = (data: RequestTransactionsInterFace) => {
    if (!loading && selectedChargingPoint) {
      dispatch(fetchTransactions({ ...data, chargingStation: selectedChargingPoint?.id }));
    }
  };
  /**
   * Function to handle data limit change
   * @param {number} changeLimit
   */
  const handleLimitChange = (changeLimit: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: changeLimit,
      chargingStation: selectedChargingPoint?.id,
      sortBy,
      sortField,
    });
  };
  /**
   * Function to handle page change
   * @param {number} pageNumber
   */
  const handlePageChange = (pageNumber: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: pageNumber,
      limit,
      sortBy,
      sortField,
    });
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortBy, sortField });
  };

  /**
   * Function for sorting table data
   * @param {any} sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField === 'time' ? 'createdAt' : sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  useEffect(() => {
    if (!selectedChargingPoint?.id) return;

    const currentId = selectedChargingPoint.id;
    const lastId = sessionStorage.getItem('TransactionsFiltersList'); // string | null

    if (lastId !== currentId || filters.length === 1) {
      dispatch(fetchTransactionsFilters({ chargingStation: currentId }));
    }
    const queryParameters = GetParametersFromCommonFilters(filters);

    if (transactions.length === 0 || lastId !== currentId || isFilterUpdated) {
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableTransactionFiltersApplied());
    }
    sessionStorage.setItem('TransactionsFiltersList', currentId);
  }, [selectedChargingPoint?.id, filters]);
  return (
    <div className="transaction-wrap">
      <div className="transaction-right-content">
        <CustomTable
          header={column}
          data={transactions}
          serialNo
          currentPage={page}
          version="v2"
          sizeChangeButtonRequired
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          filterListLoading={filterListLoading}
          filters={filters}
          filtersToDispatch={updateTransactionsFilters}
          filterListToDispatch={fetchTransactionsFilters}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportLoader={exportLoader}
          refreshCurrentPages={handleRefresh}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          topHeader={false}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          paginate
          flexDirection
          onHiddenColumnsChange={setHiddenColumns}
        />
      </div>
      <div className="transaction-left-content">
        <TransactionCharts />
      </div>
    </div>
  );
}

export default Transactions;
